import { EventEmitter } from '@angular/core';
import { isDefined } from 'ionic-angular/util/util';
import * as Q from 'q';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { Session } from '../../../core/business-objects/session/session.model';
import { OfflineError, OnlineNotAuthenticatedError } from '../../../core/error/errors';
import { VASRestService } from '../../../core/services/api/vas-rest';
import { dateToString } from '../../../core/util/date-to-string';
import { JobStatus } from '../../business-objects/job-status/job-status.model';
import { VasAppFacade } from '../vas-app-facade.service';
import { ISyncDownOptionsArgs, SyncDownOptions } from './options';
import { SyncDownOptionsEntity } from './options-entity';
import { ISyncDownOptions } from './options.interface';

export class SyncDownService {

    static readonly CLASS: string = 'SyncDownService';

    active$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    error$: Subject<Error> = new Subject();
    stage: EventEmitter<string> = new EventEmitter();

    protected logging: boolean = true;
    protected activeStatuses: Array<JobStatus> = [];
    protected busy: boolean = false;
    protected session: Session;
    protected changeQCount: number;

    constructor(
        protected appFacade: VasAppFacade,
        protected restService: VASRestService,
        protected entityList: Array<string>
    ) {
    }

    init(): void {

        this.appFacade.objects.jobStatus.store
            .getActiveList()
            .subscribe(list => {
                this.activeStatuses = list;
            });

        this.appFacade.session.getSession()
            .subscribe(session => {
                this.session = session;
            });

        this.appFacade.changeLog.getChangesWaiting().subscribe(count => {
            this.changeQCount = count;
        });
    }

    async syncFull(options: SyncDownOptions): Promise<any> {
        await this._sync(options);
    }

    async syncRefresh(options: SyncDownOptions): Promise<any> {
        const lastSyncDate = await this.getLastSyncTime(options.getAccountId());
        options.setSince(lastSyncDate);
        await this._sync(options);
    }

    prepareSyncDownOptions(args?: ISyncDownOptionsArgs): SyncDownOptions {
        const options = new SyncDownOptions(args);

        this.entityList.forEach(entity => {

            let query: object;

            if (entity === 'job' && this.activeStatuses.length) {
                query = this.getJobQuery();
            }

            options.addEntity(new SyncDownOptionsEntity({
                entity,
                query
            }));
        });

        return options;
    }

    protected getJobQuery(): object {
        return {
            jobStatus: this.activeStatuses.map(jobStatus => jobStatus.id)
        };
    }

    protected async _sync(options: SyncDownOptions): Promise<any> {

        if (!options.getAccountId()) {
            throw Error('accountId should not be empty');
        }

        const newLastSyncDown = new Date();

        this.start();

        try {
            await this.appFacade.network.refreshStatusP();
            await this.isOnlineAndAuthenticated();
            await this.syncUsers(options);
            await this.syncControlTypes(options);
            await this.syncEntities(options);
            await this.saveLastSyncDown(
                options.getAccountId(),
                newLastSyncDown
            );
            this.stop();
            return;
        } catch (error) {
            this.stop();
            return error;
        }
    }

    protected start(): void {
        this.busy = true;
        this.active$.next(true);
    }

    stop(): void {
        this.busy = false;
        this.active$.next(false);
    }

    isBusy(): boolean {
        return this.busy;
    }

    getLastSyncTime(accountId: string): Promise<Date> {
        return this.appFacade.objects.config.store
            .getLastSyncDownDate(accountId)
            .take(1)
            .map(date => {
                const d = new Date(date);
                return isNaN(d.getTime()) ? undefined : d;
            })
            .toPromise();
    }

    protected canSync(): Promise<boolean> {

        if (this.isBusy()) {
            return Promise.reject(Error('Sync down service is busy'));
        }

        if (!this.isOnline) {
            return Promise.reject(Error('Offline'));
        }

        if (this.changeQCount !== 0) {
            return Promise.reject(Error('Changes have not been synchronised'));
        }

        return Promise.resolve(true);
    }

    syncUsers(options: SyncDownOptions): Promise<any> {
        const apiRequestOptions = options.toApiRequestOptions().clone();
        apiRequestOptions.setQuery({
            limit: 2000
        });
        this.emitStageNumber(options, 1);
        return this.appFacade.objects.user.syncAll(apiRequestOptions).toPromise();
    }

    syncUser(id: string, options: SyncDownOptions): Promise<any> {
        const apiRequestOptions = options.toApiRequestOptions();
        return this.appFacade.objects.user.syncOne(id, apiRequestOptions).toPromise();
    }

    syncControlTypes(options: SyncDownOptions): Promise<any> {
        const apiRequestOptions = options.toApiRequestOptions();
        this.emitStageNumber(options, 2);
        return this.appFacade.objects.controlType.syncAll(apiRequestOptions).toPromise();
    }

    syncEntities(options: SyncDownOptions): Promise<any> {
        let stageNumber = 5;

        return new Promise((resolve, reject) => {
            options.getEntityOptions().map((entityOptions: SyncDownOptionsEntity) => {
                return () => this.syncEntity(entityOptions, stageNumber++);
            }).reduce<any>(Q.when, Q()).then(resolve, reject);
        });
    }

    syncEntity(
        options: SyncDownOptionsEntity,
        stageNumber?: number
    ): Promise<any> {
        const apiRequestOptions = options.toApiRequestOptions();
        this.emitStageNumber(options, stageNumber);
        return this.appFacade.objects
            .syncEntity(options.getEntity(), apiRequestOptions)
            .toPromise();
    }

    saveLastSyncDown(accountId: string, date: Date): Promise<any> {
        this.appFacade.objects.config.store.setLastSyncDownDate(
            accountId,
            dateToString(date)
        );
        return Promise.resolve();
    }

    isOnline(): boolean {
        return isDefined(this.session) ? this.session.getNetworkStatus().isOnline() : false;
    }

    isAuthenticated(): boolean {
        return isDefined(this.session) ? this.session.getNetworkStatus().isAuthenticated() : false;
    }

    isOnlineAndAuthenticated(): Promise<any> {
        if (!this.isOnline()) {
            return Promise.reject(new OfflineError('cannot sync at this time. network not available'));
        } else if (!this.isAuthenticated()) {
            return Promise.reject(new OnlineNotAuthenticatedError());
        }
        return Promise.resolve();
    }

    protected emitStageNumber(options: ISyncDownOptions, stageNumber?: number): void {

        if (options.getQuiet()) {
            return;
        }

        if (!isDefined(stageNumber)) {
            return;
        }

        let perc;

        try {
            perc = ((stageNumber / 12) * 100).toFixed();
        } catch (e) {
            perc = 100;
        }

        this.stage.emit(`${perc}%`);
    }
}



// WEBPACK FOOTER //
// ./src/vas/services/sync-down/sync-down.service.ts